/**
 * GenerarMasivasPage — Crear múltiples salidas a la vez (bulk).
 * Form: paquete + rango fechas + días semana + hora → POST /salidas/bulk.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Paper, Stack, Chip, Alert,
  FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } }
function authHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; }

const DIAS = [
  { v: 1, label: 'L' }, { v: 2, label: 'M' }, { v: 3, label: 'X' },
  { v: 4, label: 'J' }, { v: 5, label: 'V' }, { v: 6, label: 'S' }, { v: 0, label: 'D' },
];

export default function GenerarMasivasPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [paquetes, setPaquetes] = useState([]);
  const [puntosEmbarque, setPuntosEmbarque] = useState([]);
  const [paqueteId, setPaqueteId] = useState('');
  const [puntoId, setPuntoId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [diasSemana, setDiasSemana] = useState([1, 2, 3, 4, 5, 6, 0]); // todos
  const [horaSalida, setHoraSalida] = useState('08:00');
  const [horaRegreso, setHoraRegreso] = useState('15:00');
  const [capacidad, setCapacidad] = useState('50');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);

  const cargar = useCallback(async () => {
    try {
      const [pRes, peRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/turismo/puntos-embarque?empresa_id=${empresaId}`, { headers: authHeaders() }),
      ]);
      setPaquetes(await pRes.json());
      setPuntosEmbarque(await peRes.json());
    } catch (e) { toast.error('Error cargando datos'); }
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  // Recalcular preview de fechas
  useEffect(() => {
    if (!fechaDesde || !fechaHasta) { setPreview([]); return; }
    const start = new Date(fechaDesde + 'T00:00:00');
    const end = new Date(fechaHasta + 'T00:00:00');
    if (end < start) { setPreview([]); return; }
    const list = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (diasSemana.includes(d.getDay())) {
        list.push(new Date(d));
      }
      if (list.length > 365) break;
    }
    setPreview(list);
  }, [fechaDesde, fechaHasta, diasSemana]);

  const toggleDia = (v) => {
    setDiasSemana(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const handleGenerar = async () => {
    if (!paqueteId || !fechaDesde || !fechaHasta || preview.length === 0) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      const fechas = preview.map(d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
      const r = await fetch(`${API_BASE}/turismo/salidas/bulk`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          empresa_id: empresaId,
          paquete_id: parseInt(paqueteId),
          punto_embarque_id: puntoId ? parseInt(puntoId) : null,
          fechas,
          hora_salida: horaSalida,
          hora_regreso: horaRegreso,
          capacidad_total: parseInt(capacidad),
          estado: 'programada',
        }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      const data = await r.json();
      toast.success(`${data.creadas || preview.length} salidas creadas`);
      refreshCounts?.();
      setPreview([]); setFechaDesde(''); setFechaHasta('');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Generar salidas masivas</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Programa varias fechas a la vez con el mismo paquete y horario.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <TextField select size="small" label="Paquete *" value={paqueteId} onChange={e => setPaqueteId(e.target.value)}>
            {paquetes.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Punto de embarque" value={puntoId} onChange={e => setPuntoId(e.target.value)}>
            <MenuItem value="">— Ninguno —</MenuItem>
            {puntosEmbarque.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            <TextField size="small" type="date" label="Desde *" value={fechaDesde}
              onChange={e => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta *" value={fechaHasta}
              onChange={e => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, color: '#475569', mb: 0.5, fontWeight: 700 }}>Días de la semana</Typography>
            <Stack direction="row" spacing={0.5}>
              {DIAS.map(d => {
                const sel = diasSemana.includes(d.v);
                return (
                  <Chip key={d.v} label={d.label} onClick={() => toggleDia(d.v)}
                    sx={{ width: 36, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                           bgcolor: sel ? '#0d9488' : '#e2e8f0',
                           color: sel ? '#fff' : '#64748b',
                           '&:hover': { bgcolor: sel ? '#0f766e' : '#cbd5e1' } }} />
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
            <TextField size="small" type="time" label="Hora salida" value={horaSalida}
              onChange={e => setHoraSalida(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="time" label="Hora regreso" value={horaRegreso}
              onChange={e => setHoraRegreso(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="number" label="Capacidad" value={capacidad}
              onChange={e => setCapacidad(e.target.value)} />
          </Box>

          <Divider />

          {preview.length > 0 ? (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Se crearán <strong>{preview.length}</strong> salidas: del {fechaDesde} al {fechaHasta},
              los días seleccionados.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              Configura el rango de fechas y los días para previsualizar.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" disableElevation startIcon={<EventRepeatIcon />}
              onClick={handleGenerar} disabled={saving || preview.length === 0}
              sx={{ textTransform: 'none', fontWeight: 700,
                     background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {saving ? 'Generando…' : `Generar ${preview.length} salidas`}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <ToastContainer />
    </Box>
  );
}
